import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import i18n from "@/i18n";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ brand_name: "EVOSOFT", logo_data_url: null, name: "" });

  const applyUserLanguage = useCallback((data) => {
    const lang = data?.preferred_language;
    if (lang && ["it", "en", "fr"].includes(lang) && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
    } catch {
      setSettings({ brand_name: "EVOSOFT", logo_data_url: null, name: "" });
    }
  }, []);

  const check = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      applyUserLanguage(data);
      await loadSettings();
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [loadSettings, applyUserLanguage]);

  useEffect(() => { check(); }, [check]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      applyUserLanguage(data);
      await loadSettings();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data);
      applyUserLanguage(data);
      await loadSettings();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
  };

  const updateLanguage = async (lang) => {
    try {
      await api.patch("/auth/me/language", { language: lang });
      await i18n.changeLanguage(lang);
      setUser((u) => (u ? { ...u, preferred_language: lang } : u));
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh: check, settings, reloadSettings: loadSettings, updateLanguage }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
